// ✅ ChantierPage.js avec colonne société + filtre + champ formulaire
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import { FaIndustry, FaMapMarkerAlt, FaTrafficLight, FaRegStickyNote, FaTools, FaPlus, FaEdit, FaTrash, FaBuilding } from 'react-icons/fa';

const ChantierPage = ({ user }) => {
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [pageError, setPageError] = useState(null);
  const [formError, setFormError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    status: '',
    observation: '',
    societe: 'PISTAS'
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchChantiers();
    }
  }, [user]);

  const fetchChantiers = async () => {
    try {
      const res = await axios.get('/chantiers');
      setChantiers(res.data);
    } catch (err) {
      setPageError('❌ Erreur de chargement des chantiers');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setFormMode('add');
    setFormData({ name: '', location: '', status: '', observation: '', societe: 'PISTAS' });
    setShowFormModal(true);
  };

  const openEditModal = (chantier) => {
    setFormMode('edit');
    setSelected(chantier);
    setFormData({
      name: chantier.name,
      location: chantier.location || '',
      status: chantier.status || '',
      observation: chantier.observation || '',
      societe: chantier.societe || 'PISTAS'
    });
    setShowFormModal(true);
  };

  const openDeleteModal = (chantier) => {
    setSelected(chantier);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async () => {
    try {
      if (formMode === 'add') {
        await axios.post('/chantiers', formData);
      } else {
        await axios.put(`/chantiers/${selected.id}`, formData);
      }
      setShowFormModal(false);
      fetchChantiers();
    } catch (err) {
      setFormError(err.response?.data?.message || '❌ Erreur enregistrement');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/chantiers/${selected.id}`);
      setShowDeleteModal(false);
      fetchChantiers();
    } catch (err) {
      setPageError(err.response?.data?.message || '❌ Erreur suppression');
    }
  };

  if (!user || !user.role) {
    return <Alert variant="danger" className="m-4">⛔ Utilisateur non reconnu</Alert>;
  }

  if (user.role !== 'admin') {
    return <Alert variant="danger" className="m-4">⛔ Accès réservé aux administrateurs C</Alert>;
  }

  const columns = [
    { dataField: 'id', text: '🆔 ID', sort: true },
    {
      dataField: 'name',
      text: <><FaIndustry className="me-1" /> Nom</>,
      sort: true,
      filter: textFilter({ placeholder: '🔍 Nom...', className: 'form-control form-control-sm' })
    },
    {
      dataField: 'location',
      text: <><FaMapMarkerAlt className="me-1" /> Lieu</>,
      sort: true,
      filter: textFilter({ placeholder: '🔍 Lieu...', className: 'form-control form-control-sm' })
    },
    {
      dataField: 'status',
      text: <><FaTrafficLight className="me-1" /> Statut</>,
      sort: true,
      filter: textFilter({ placeholder: '🔍 Statut...', className: 'form-control form-control-sm' })
    },
    {
      dataField: 'societe',
      text: <><FaBuilding className="me-1" /> Société</>,
      sort: true,
      filter: textFilter({ placeholder: '🔍 Société...', className: 'form-control form-control-sm' })
    },
    {
      dataField: 'observation',
      text: <><FaRegStickyNote className="me-1" /> Observation</>,
      formatter: cell => cell || '—'
    },
    {
      dataField: 'actions',
      text: <><FaTools className="me-1" /> Actions</>,
      formatter: (_, row) => (
        <>
          <Button variant="primary" size="sm" className="me-2" onClick={() => openEditModal(row)}><FaEdit /></Button>
          <Button variant="danger" size="sm" onClick={() => openDeleteModal(row)}><FaTrash /></Button>
        </>
      ),
      headerAlign: 'center',
      align: 'center'
    }
  ];

  return (
    <div className="container mt-4">
      <h2><FaIndustry className="me-2" />Gestion des chantiers</h2>
      {pageError && (
        <Alert variant="danger" dismissible onClose={() => setPageError(null)} className="mb-3">
          {pageError}
        </Alert>
      )}
      <div className="d-flex justify-content-end mb-3">
        <Button variant="success" onClick={openAddModal}><FaPlus className="me-1" /> Ajouter</Button>
      </div>

      {loading ? (
        <p>⏳ Chargement...</p>
      ) : (
        <BootstrapTable
          keyField="id"
          data={chantiers}
          columns={columns}
          pagination={paginationFactory()}
          filter={filterFactory()}
          striped
          hover
          condensed
          noDataIndication="Aucun chantier trouvé ❌"
        />
      )}

      <Modal show={showFormModal} onHide={() => { setShowFormModal(false); setFormError(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>{formMode === 'add' ? <><FaPlus /> Ajouter un chantier</> : <><FaEdit /> Modifier un chantier</>}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger" className="mb-3">{formError}</Alert>}
          <Form>
            <Form.Group><Form.Label>Nom</Form.Label><Form.Control name="name" value={formData.name} onChange={handleFormChange} /></Form.Group>
            <Form.Group><Form.Label>Lieu</Form.Label><Form.Control name="location" value={formData.location} onChange={handleFormChange} /></Form.Group>
            <Form.Group><Form.Label>Statut</Form.Label><Form.Control name="status" value={formData.status} onChange={handleFormChange} /></Form.Group>
            <Form.Group><Form.Label>Société</Form.Label>
              <Form.Select name="societe" value={formData.societe} onChange={handleFormChange}>
                <option value="PISTAS">PISTAS</option>
                <option value="RUTAS">RUTAS</option>
              </Form.Select>
            </Form.Group>
            <Form.Group><Form.Label>Observation</Form.Label><Form.Control as="textarea" name="observation" value={formData.observation} onChange={handleFormChange} /></Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowFormModal(false); setFormError(null); }}>Annuler</Button>
          <Button variant="primary" onClick={handleFormSubmit}>Enregistrer</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title><FaTrash className="me-2" /> Supprimer le chantier</Modal.Title>
        </Modal.Header>
        <Modal.Body>Confirmer la suppression de <strong>{selected?.name}</strong> ?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete}>Supprimer</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChantierPage;